import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { formatAmountInput, parseAmountInput } from '@brimo/shared';
import { clsx } from '../clsx';
import { useFieldIds } from '../hooks';
import { Caret } from './Caret';
import { FieldFrame } from './FieldFrame';
import styles from './TextField.module.css';

export type TextFieldType =
  'text' | 'email' | 'password' | 'tel' | 'url' | 'search' | 'number' | 'money' | 'date' | 'time';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label: string;
  /** Keeps the label for assistive technology only. */
  labelHidden?: boolean | undefined;
  /** `money`: decimal text with "€" suffix, thousands formatting on blur, `onCentsChange`; `date`/`time`: native pickers with the field chrome. */
  type?: TextFieldType | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  /** Error border without a message (the message lives in a banner, Shell 18). */
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  optional?: boolean | undefined;
  labelEnd?: ReactNode | undefined;
  leading?: ReactNode | undefined;
  trailing?: ReactNode | undefined;
  size?: 'default' | 'compact' | undefined;
  tone?: 'card' | 'sunken' | undefined;
  /** money only: called with the parsed amount in cents (null when empty or unreadable) on every change and blur. */
  onCentsChange?: ((cents: number | null) => void) | undefined;
}

/** Text/e-mail/password/number/money/date/time field with the field state of the screens (accent border + focus ring). */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    labelHidden,
    type = 'text',
    hint,
    error,
    invalid,
    required,
    optional,
    labelEnd,
    leading,
    trailing,
    size,
    tone,
    onCentsChange,
    className,
    id,
    readOnly,
    disabled,
    onChange,
    onBlur,
    value,
    defaultValue,
    ...rest
  },
  ref,
) {
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const isMoney = type === 'money';
  const isPicker = type === 'date' || type === 'time';
  const controlled = value !== undefined;
  const [moneyText, setMoneyText] = useState(() =>
    typeof defaultValue === 'string' || typeof defaultValue === 'number' ? String(defaultValue) : '',
  );

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (isMoney) {
      if (!controlled) setMoneyText(event.target.value);
      onCentsChange?.(parseAmountInput(event.target.value));
    }
    onChange?.(event);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    if (isMoney) {
      const cents = parseAmountInput(event.target.value);
      const formatted =
        cents === null ? (event.target.value.trim() ? event.target.value : '') : formatAmountInput(cents);
      if (formatted !== event.target.value) {
        if (controlled) {
          // React tracks the value it wrote itself; only the prototype setter plus an input event reaches `onChange`.
          // eslint-disable-next-line @typescript-eslint/unbound-method -- the setter is applied explicitly below
          const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
          if (setter) {
            Reflect.apply(setter, event.target, [formatted]);
            event.target.dispatchEvent(new Event('input', { bubbles: true }));
          }
        } else {
          setMoneyText(formatted);
        }
      }
      onCentsChange?.(cents);
    }
    onBlur?.(event);
  }

  function openPicker() {
    const input = inputRef.current;
    if (!input || readOnly || disabled) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  }

  const nativeType = isMoney ? 'text' : type;
  const inputMode = isMoney ? 'decimal' : rest.inputMode;
  const trailingNode = isMoney ? (
    <>
      {trailing}
      <span className={styles.suffix}>€</span>
    </>
  ) : isPicker ? (
    <>
      {trailing}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        className={styles.pickerButton}
        onMouseDown={(event) => event.preventDefault()}
        onClick={openPicker}
      >
        <Caret size={11} />
      </button>
    </>
  ) : (
    trailing
  );

  return (
    <FieldFrame
      htmlFor={ids.inputId}
      label={label}
      labelHidden={labelHidden}
      required={required}
      optional={optional}
      labelEnd={labelEnd}
      hint={hint}
      error={error}
      hintId={ids.hintId}
      errorId={ids.errorId}
      invalid={invalid}
      readOnly={readOnly}
      disabled={disabled}
      leading={leading}
      trailing={trailingNode}
      size={size}
      tone={tone}
      className={className}
    >
      <input
        ref={inputRef}
        id={ids.inputId}
        type={nativeType}
        inputMode={inputMode}
        className={clsx(
          styles.input,
          isPicker && styles.picker,
          (isMoney || type === 'number') && styles.numeric,
        )}
        aria-invalid={error || invalid ? true : undefined}
        aria-describedby={ids.describedBy}
        aria-required={required || undefined}
        readOnly={readOnly}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        {...(isMoney && !controlled ? { value: moneyText } : controlled ? { value } : { defaultValue })}
        {...rest}
      />
    </FieldFrame>
  );
});
